import { Box, MenuItem, SxProps, Typography } from '@mui/material'
import Menu from '@mui/material/Menu'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BesseggenLogo from '../../assets/icons/BesseggenLogo'
import { BROKERS_LOGOS_BASE_URL } from '../../assets/constants'
import { useTradingAccountContext } from '../../hooks/useTradingAccountContext'
import { TradingAccount, TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { selectBrokers } from '../../selectors/user'
import { setPrimaryTradingAccountApi } from '../../services/apis/tradingAccount'
import { isBesseggenBrokerCode, isPaperBrokerCode, isRealTradingAccount, isTradingAccountVisibleForBackend, getTradingAccountSelectorLabel } from '../../util/tradingAccounts'
import {
    getBesseggenSwitchMenuItem,
    isBesseggenAccountSwitchable,
    showSwitchToRealTradeOption
} from '../../util/tradingAccountContext'
import { ExtraSmallButton, InteractiveTooltip } from '../custom'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import BrokerIcon from './BrokerIcon'
import OpenDematAccountDialog from '../../pages/settings/tradingAccountProfile/OpenDematAccountDialog'
import OpenTradingAccountDialog from './OpenTradingAccountDialog'
import { isShoonyaBackend } from '../../util/backendProvider'

type Props = {
    compact?: boolean
    disabled?: boolean
    defaultTradingAccountId?: string
    fullWidth?: boolean
    sx?: SxProps
    closeQuickTradeWindow?: () => void
}

const BESSEGGEN_ICON_SX = { fontSize: '25px', display: 'block' } as const

const BesseggenIconOnly = ({
    compact,
    sx,
    tooltip
}: {
    compact?: boolean
    sx?: SxProps
    tooltip?: string
}) => {
    const icon = (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: compact ? 32 : undefined,
                height: compact ? 32 : undefined,
                ...sx
            }}
        >
            <BesseggenLogo sx={BESSEGGEN_ICON_SX} />
        </Box>
    )

    if (!tooltip) {
        return icon
    }

    return (
        <InteractiveTooltip title={tooltip} arrow placement={compact ? 'left' : 'top'}>
            <span style={{ display: 'inline-flex' }}>{icon}</span>
        </InteractiveTooltip>
    )
}

const TradingAccountSelector = ({
    compact = false,
    disabled = false,
    defaultTradingAccountId = '',
    fullWidth = false,
    sx = {},
    closeQuickTradeWindow
}: Props) => {
    const {
        tradingAccounts,
        primaryTradingAccount,
        besseggenAccount,
        practiceEnabled,
        mode,
        profileClientCode
    } = useTradingAccountContext()
    const brokers = useSelector(selectBrokers)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [openTradingDialog, setOpenTradingDialog] = useState(false)
    const [openDematDialog, setOpenDematDialog] = useState(false)

    const open = Boolean(anchorEl)

    const widgetTradingAccount = useMemo(() => {
        if (disabled) {
            return tradingAccounts.find((t) => t.accountId === defaultTradingAccountId) ?? primaryTradingAccount
        }
        return primaryTradingAccount
    }, [disabled, primaryTradingAccount, tradingAccounts, defaultTradingAccountId])

    const brokerImageUrlFromCode = useCallback(
        (code: string) => {
            const broker = brokers.find((b) => b.code === code)
            return broker ? BROKERS_LOGOS_BASE_URL + broker.imageUrl : ''
        },
        [brokers]
    )

    const handleClose = useCallback(
        (closeWindow: boolean = false) => {
            setAnchorEl(null)
            if (closeWindow && closeQuickTradeWindow) {
                closeQuickTradeWindow()
            }
        },
        [closeQuickTradeWindow]
    )

    const changePrimaryTradingAccount = useCallback(
        async (account: TradingAccount) => {
            if (account.accountId !== primaryTradingAccount?.accountId) {
                try {
                    await setPrimaryTradingAccountApi({
                        body: {
                            tradingAccountId: account.accountId
                        }
                    })
                } catch (e) {
                    console.error(e, 'error in change primary account')
                }
            }
        },
        [primaryTradingAccount?.accountId]
    )

    const handleSwitchToRealTrade = useCallback(() => {
        handleClose(true)
        if (besseggenAccount && isBesseggenAccountSwitchable(besseggenAccount.status)) {
            void changePrimaryTradingAccount(besseggenAccount)
            return
        }
        setOpenDematDialog(true)
    }, [besseggenAccount, changePrimaryTradingAccount, handleClose])

    const realTradeMenuItem = getBesseggenSwitchMenuItem(besseggenAccount)
    const showRealTradeOption = showSwitchToRealTradeOption(practiceEnabled, besseggenAccount)

    const switchableAccounts = useMemo(
        () =>
            tradingAccounts.filter((account) => {
                if (!isTradingAccountVisibleForBackend(account.brokerCode)) {
                    return false
                }
                if (isPaperBrokerCode(account.brokerCode)) {
                    return practiceEnabled
                }
                if (isBesseggenBrokerCode(account.brokerCode)) {
                    return isBesseggenAccountSwitchable(account.status)
                }
                return account.status === TradingAccountStatus.ACTIVE
            }),
        [practiceEnabled, tradingAccounts]
    )

    const renderDialogs = () => (
        <>
            <OpenTradingAccountDialog
                open={openTradingDialog}
                onClose={() => setOpenTradingDialog(false)}
            />
            <OpenDematAccountDialog
                open={openDematDialog}
                onClose={() => setOpenDematDialog(false)}
            />
        </>
    )

    const accountLabel = (account: TradingAccount) =>
        getTradingAccountSelectorLabel(account, profileClientCode)

    if (mode === 'besseggen-icon') {
        const realAccountTooltip =
            primaryTradingAccount && isRealTradingAccount(primaryTradingAccount.brokerCode)
                ? accountLabel(primaryTradingAccount)
                : undefined
        return <BesseggenIconOnly compact={compact} sx={sx} tooltip={realAccountTooltip} />
    }

    if (mode === 'no-accounts') {
        if (isShoonyaBackend()) {
            return null
        }
        return (
            <>
                <InteractiveTooltip title="Start trading" arrow placement={compact ? 'left' : 'top'}>
                    <span style={{ display: 'inline-flex' }}>
                        <ExtraSmallButton
                            variant="outlined"
                            size="small"
                            onClick={() => setOpenTradingDialog(true)}
                            disabled={disabled}
                            sx={compact ? { width: '32px', height: '32px', minWidth: '32px', ...sx } : { ...sx }}
                        >
                            <BesseggenLogo sx={BESSEGGEN_ICON_SX} />
                        </ExtraSmallButton>
                    </span>
                </InteractiveTooltip>
                {renderDialogs()}
            </>
        )
    }

    const accountButton = (
        <ExtraSmallButton
            variant="outlined"
            size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={disabled}
            endIcon={compact ? undefined : <ThinArrowDownIcon />}
            sx={fullWidth ? { width: '100%', ...sx } : {
                ...(compact ? { width: '32px', minWidth: '32px' } : {}),
                ...sx
            }}
        >
            {widgetTradingAccount ? (
                <BrokerIcon
                    brokerCode={widgetTradingAccount.brokerCode}
                    imageUrl={brokerImageUrlFromCode(widgetTradingAccount.brokerCode)}
                    sx={{ fontSize: '25px', marginRight: compact ? 0 : '5px' }}
                />
            ) : (
                <BesseggenLogo sx={BESSEGGEN_ICON_SX} />
            )}
        </ExtraSmallButton>
    )

    return (
        <span style={fullWidth ? { width: '100%' } : {}}>
            {compact ? (
                <InteractiveTooltip title={widgetTradingAccount ? accountLabel(widgetTradingAccount) : 'Switch account'} arrow placement="left">
                    <span style={{ display: 'inline-flex' }}>{accountButton}</span>
                </InteractiveTooltip>
            ) : (
                accountButton
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                slotProps={{
                    paper: {
                        sx: {
                            width: Math.max(anchorEl?.clientWidth ?? 0, 220),
                            minWidth: '220px',
                            '& .MuiList-root': {
                                padding: 0
                            }
                        }
                    }
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                {switchableAccounts.map((account) => (
                    <MenuItem
                        selected={account.accountId === widgetTradingAccount?.accountId}
                        key={account.accountId}
                        onClick={() => {
                            void changePrimaryTradingAccount(account)
                            handleClose()
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1
                        }}
                    >
                        <BrokerIcon
                            brokerCode={account.brokerCode}
                            imageUrl={brokerImageUrlFromCode(account.brokerCode)}
                            sx={{ fontSize: '25px', marginRight: '5px' }}
                        />
                        <Typography variant="body2" noWrap fontSize="14px" sx={{ flexGrow: 1 }}>
                            {accountLabel(account)}
                        </Typography>
                    </MenuItem>
                ))}
                {showRealTradeOption && !isShoonyaBackend() && (
                    <MenuItem
                        disabled={realTradeMenuItem.disabled}
                        onClick={() => {
                            if (!realTradeMenuItem.disabled) {
                                handleSwitchToRealTrade()
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1
                        }}
                    >
                        <BesseggenLogo sx={{ fontSize: '25px', marginRight: '5px' }} />
                        <Typography variant="body2" noWrap fontSize="14px" sx={{ flexGrow: 1 }}>
                            {realTradeMenuItem.label}
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
            {renderDialogs()}
        </span>
    )
}

export default TradingAccountSelector
